import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";

import { Btn, H3, MUIIcons } from "../../../../AbstractElements";
import Divider from "../../../../CommonElements/Divider";
import FilterSliderModal from "../../../../CommonElements/FilterSliderModal";
import JustifyTabs from "../../../../CommonElements/JustifyTabs";
import CommonModal from "../../../../CommonElements/Modal";
import SearchBar from "../../../../CommonElements/SearchBar";
import LearningPlanCard from "../../../../container/LearningPlanCard";
import NoDataFound from "../../../../container/NoDataFound";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  getLearningPlanByDomainId,
  getLearningPlanById,
  setLearningPlanMasterToInitials,
  updateLearningPlan,
} from "../../../../ReduxToolkit/Reducers/LearningPlanMaster";
import { getJobRoleList } from "../../../../ReduxToolkit/Reducers/ManageMasterDataSlice";
import {
  getFilterRolesList,
  getProficiencyList,
} from "../../../../ReduxToolkit/Reducers/MasterSlice";
import {
  AddNew,
  ArchiveLearningPlan,
  ArchiveLearningPlanConfirmation,
  Confirm,
  JobRole,
  LearningPlansTitle,
  Level,
  Select,
  UnarchiveLearningPlan,
  UnarchiveLearningPlanConfirmation,
} from "../../../../utils/Constant";
import {
  activeLearningPlanTabs,
  extractFileName,
  fileUrlKeys,
  formatMinutes,
} from "../../../../utils/helper/helper";
import useIsMobile from "../../../../utils/helper/responsive";

import "./style.scss";

const ActiveLearningPlans = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { roleOnFilter, proficiencyList } = useAppSelector(
    state => state.master
  );
  const { learningPlanByDomainId, learningPlanById } = useAppSelector(
    state => state.learningPlanMaster
  );
  const { jobRoleList } = useAppSelector(state => state.manageMasterData);
  const [roleFilterList, setRoleFilterList] = useState([]);
  const [proficiencyListData, setProficiencyListData] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const learningPlanId = queryParams.get("id");
  const [activeTab, setActiveTab] = useState<string>("active");
  const [selectedModalType, setSelectedModalType] = useState("");
  const [selectedModalId, setSelectedModalId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clearData, setClearData] = useState(false);
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [transformedData, setTransformedData] = useState({});

  useEffect(() => {
    dispatch(getFilterRolesList());
    dispatch(getProficiencyList());
    dispatch(getJobRoleList());
  }, [dispatch]);

  useEffect(() => {
    if (selectedModalId) {
      dispatch(getLearningPlanById({ id: selectedModalId }));
    }
  }, [dispatch, selectedModalId]);

  useEffect(() => {
    if (jobRoleList?.job_role && Array.isArray(jobRoleList?.job_role)) {
      const result = jobRoleList.job_role.reduce((acc, item) => {
        acc[item.id] = {
          name: item.name,
          industry_name: item.industry_name,
        };
        return acc;
      }, {});
      setTransformedData(result);
    }
  }, [jobRoleList]);

  useEffect(() => {
    const plans = [];
    if (learningPlanByDomainId?.learning_plans) {
      learningPlanByDomainId?.learning_plans?.map(item => {
        plans.push({
          id: item?.id,
          name: item?.name,
          level: item?.level,
          job_roles: item?.job_roles,
          courses: item?.courses,
          assessments: item?.assessments,
          total_duration: item?.total_duration,
          users: item?.users,
          user_groups: item?.user_groups,
          thumbnail_url: item?.thumbnail_url,
        });
      });
    }
    setData(plans);
  }, [learningPlanByDomainId]);

  useEffect(() => {
    if (learningPlanId) {
      dispatch(
        getLearningPlanByDomainId({
          domain_id: learningPlanId,
          status: activeTab,
        })
      );
    }
  }, [learningPlanId, activeTab]);

  const toggleModal = () => {
    if (isModalOpen) {
      setSelectedModalId("");
    }
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    const levels = [];
    proficiencyList?.proficiency?.forEach(sites => {
      const userObj = {
        label: sites?.level_name,
        value: sites?.level_name,
        name: sites?.id.toString(),
      };
      levels.push(userObj);
    });
    setProficiencyListData(levels);
  }, [proficiencyList]);

  useEffect(() => {
    const value = [];
    if (roleOnFilter) {
      roleOnFilter?.job_role?.forEach(item => {
        value.push({
          name: item?.id,
          label: item?.name,
          value: item?.name,
        });
      });
    }
    setRoleFilterList(value);
  }, [roleOnFilter]);

  const handleTabClick = (tabId: any) => {
    setClearData(true);
    setActiveTab(tabId);
    setSearchQuery("");
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query.toLowerCase());
    setClearData(false);
  };

  const handleDone = async (values: { [key: string]: string[] }) => {
    setClearData(true);
    if (values["role"] || values["level"]) {
      dispatch(
        getLearningPlanByDomainId({
          domain_id: learningPlanId,
          status: activeTab,
          role: values["role"]?.toString() || null,
          level: values["level"]?.toString() || null,
        })
      );
    }
  };

  const handleClear = async () => {
    setClearData(true);
    dispatch(
      getLearningPlanByDomainId({
        domain_id: learningPlanId,
        status: activeTab,
      })
    );
    setSelectedFilterValues({});
    setDefaultFilterValues({});
  };

  let sizeTitle = "";
  if (selectedModalType === "unarchive") {
    sizeTitle = ArchiveLearningPlan;
  } else if (selectedModalType === "archive") {
    sizeTitle = UnarchiveLearningPlan;
  }

  const renderModalBody = type => {
    if (type === "unarchive") {
      return <p>{ArchiveLearningPlanConfirmation}</p>;
    } else if (type === "archive") {
      return <p>{UnarchiveLearningPlanConfirmation}</p>;
    }
  };

  const filteredData = data?.filter(item =>
    item.name?.toLowerCase().includes(searchQuery)
  );

  const extractJobRoleName = ids =>
    ids?.map(id => transformedData[id]?.name || null);

  return (
    <div className="page-body">
      <CommonModal
        sizeTitle={sizeTitle}
        modalBodyClassName=""
        isOpen={isModalOpen}
        toggle={toggleModal}
        backdrop="static"
        size="lg"
        showFooter
        onPrimaryBtnClick={async () => {
          const payloadData: any = { ...learningPlanById };
          payloadData.domain_id =
            payloadData?.domains?.map(item => item?.id) || [];
          payloadData.thumbnail_url =
            fileUrlKeys?.thumbnails +
            "/" +
            extractFileName(payloadData?.thumbnail_url);
          if (selectedModalType === "archive") {
            payloadData.status = "active";
            await dispatch(
              updateLearningPlan({
                payload: payloadData,
                type: "edit_learning_plan_details",
              })
            );
          }
          if (selectedModalType === "unarchive") {
            payloadData.status = "archive";
            await dispatch(
              updateLearningPlan({
                payload: payloadData,
                type: "edit_learning_plan_details",
              })
            );
          }
          await dispatch(
            getLearningPlanByDomainId({
              domain_id: learningPlanId,
              status: activeTab,
            })
          );
          toggleModal();
        }}
        primaryBtnText={Confirm}
      >
        {renderModalBody(selectedModalType)}
      </CommonModal>
      <Card className="p-1">
        <CardBody>
          <Row
            className={`d-flex flex-column flex-md-row justify-content-between ${!isMobile ? "align-items-center" : ""}`}
          >
            <Col
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="mb-1 mb-sm-0 d-flex flex-column gap-2 "
            >
              <JustifyTabs
                tabs={activeLearningPlanTabs}
                activeTab={activeTab}
                onTabClick={handleTabClick}
              />
            </Col>

            <Col
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="d-flex flex-wrap justify-content-end align-items-center gap-3 ms-auto"
            >
              <div className="w-100 d-block d-sm-none mb-2">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                />
              </div>
              <div className="d-none d-sm-block">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                />
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <div className="mt-2">
                  <FilterSliderModal
                    dropdowns={[
                      {
                        label: JobRole,
                        key: "role",
                        tooltipText: `${Select} ${JobRole}`,
                        options: roleFilterList,
                        isMultiSelect: true,
                      },
                      {
                        label: Level,
                        key: "level",
                        tooltipText: `${Select} ${Level}`,
                        options: proficiencyListData,
                        isMultiSelect: true,
                      },
                    ]}
                    selectedFilterValues={selectedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    setSelectedFilterValues={setSelectedFilterValues}
                    setDefaultFilterValues={setDefaultFilterValues}
                    onDone={handleDone}
                    onClear={handleClear}
                  />
                </div>
                <Link
                  to={`/master-data/create-learning-plan?domain_id=${learningPlanId}`}
                  onClick={() => {
                    dispatch(setLearningPlanMasterToInitials());
                  }}
                >
                  <Btn
                    icon={
                      <MUIIcons iconName="AddCircleOutlineOutlined" size={16} />
                    }
                    onClick={() => {
                      dispatch(setLearningPlanMasterToInitials());
                    }}
                    color="primary"
                  >
                    {AddNew}
                  </Btn>
                </Link>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <H3 className="mt-2">{`${learningPlanByDomainId?.domain} : ${LearningPlansTitle}`}</H3>
          <Divider />
          {filteredData?.length > 0 ? (
            <div className="learning-plan-grid">
              {filteredData?.map(item => {
                return (
                  <LearningPlanCard
                    key={item.id}
                    id={item.id}
                    planType={item?.level}
                    title={item?.name}
                    creator={extractJobRoleName(item?.job_roles)}
                    totalDuration={formatMinutes(item?.total_duration)}
                    learnersCount={item?.users}
                    groupsCount={item?.user_groups}
                    thumbnailUrl={item?.thumbnail_url}
                    isArchived={activeTab === "archive"}
                    courses={item?.courses}
                    assessments={item?.assessments}
                    onEditDetails={() => {
                      return navigate(
                        `/master-data/create-learning-plan?id=${item.id}&domain_id=${learningPlanId}&show=details`
                      );
                    }}
                    onArchiveToggle={() => {
                      setSelectedModalId(item.id);
                      setSelectedModalType(
                        activeTab === "active" ? "unarchive" : "archive"
                      );
                      toggleModal();
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <NoDataFound />
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ActiveLearningPlans;
